import React, { useState } from 'react';
import { X, Clock, DollarSign, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StartShiftDialog({ open, onClose, onShiftStarted }) {
  const [startingCash, setStartingCash] = useState('');
  const [displayCash, setDisplayCash] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const handleCashInput = (value) => {
    // Remove all non-digit characters
    const rawValue = value.replace(/\D/g, '');
    setStartingCash(rawValue);
    
    // Format for display
    if (rawValue) {
      setDisplayCash(formatNumber(rawValue));
    } else {
      setDisplayCash('');
    }
  };

  const handleStartShift = async () => {
    if (!startingCash || parseFloat(startingCash) < 0) {
      toast.error('Masukkan kas awal yang valid');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/shifts/start`,
        {
          starting_cash: parseFloat(startingCash),
          notes: notes.trim()
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        // Toast will be shown by parent component (KasirDashboard)
        onShiftStarted(response.data.shift);
        onClose();
      }
    } catch (error) {
      console.error('Error starting shift:', error);
      toast.error(error.response?.data?.detail || 'Gagal memulai shift');
    } finally {
      setLoading(false);
    }
  };

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-gradient-to-br from-slate-800 via-slate-800 to-slate-900 border-2 border-slate-700 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
              <Clock className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-black text-white">Mulai Shift</h3>
              <p className="text-xs text-blue-200">Selamat datang, {user.full_name}!</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Info Box */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-blue-300 mb-1">Mulai shift baru</p>
                <p className="text-xs text-white/70">
                  Masukkan jumlah kas awal untuk memulai shift Anda. Sistem akan menghitung otomatis di akhir shift.
                </p>
              </div>
            </div>
          </div>

          {/* Starting Cash Input */}
          <div>
            <label className="block text-white text-sm font-bold mb-2 flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-400" />
              Kas Awal
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 font-semibold">
                Rp
              </span>
              <input
                type="text"
                value={displayCash}
                onChange={(e) => handleCashInput(e.target.value)}
                placeholder="0"
                className="w-full pl-12 pr-4 py-3 bg-slate-900/50 border-2 border-slate-600/50 rounded-xl text-white text-lg font-bold placeholder-white/30 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                autoFocus
              />
            </div>
            <p className="text-xs text-white/50 mt-2">
               Hitung semua uang kas di laci sebelum memulai
            </p>
          </div>

          {/* Notes (Optional) */}
          <div>
            <label className="block text-white text-sm font-bold mb-2">
              Catatan (opsional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Tambahkan catatan jika diperlukan..."
              rows="3"
              className="w-full px-4 py-3 bg-slate-900/50 border-2 border-slate-600/50 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-900/50 px-6 py-4 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white font-bold rounded-xl transition-all"
          >
            Batal
          </button>
          <button
            onClick={handleStartShift}
            disabled={loading || !startingCash}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 disabled:from-gray-600 disabled:to-gray-600 text-white font-bold rounded-xl transition-all shadow-lg hover:shadow-green-500/50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                Memulai...
              </>
            ) : (
              <>
                <Clock className="h-5 w-5" />
                Mulai Shift
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
